import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Stepper } from "components/Stepper";
import { Heading } from "common/ui/Heading";
import { Stack } from "common/ui/Stack";
import { Sidebar } from "common/ui/Sidebar";
import { Button } from "common/ui/Button";
import { ErrorDisplay } from "common/ui/ErrorDisplay";
import { BodyFrame } from "common/ui/BodyFrame";

import styles from "./CreateListingLayout.module.scss";

interface CreateListingLayoutProps {
  children: React.ReactNode;
  action: {
    text: string;
    onClick: () => void;
    isLoading?: boolean;
  };
  error?: string;
}

export const CreateListingLayout = ({ children, action, error }: CreateListingLayoutProps) => {
  const { t } = useTranslation("marketplace");
  const { pathname } = useRouter();

  return (
    <div className={styles.CreateListingLayout}>
      <Sidebar className={styles.Sidebar}>
        <BodyFrame className={styles.SidebarContent}>
          <Stack direction="column" gap={48}>
            <Heading variant="h1">{t("createListingLayout.createListing")}</Heading>
            <Stepper>
              <Stepper.Step
                href="/create"
                stepNo={1}
                isCurrentStep={pathname === "/create"}
                isComplete={pathname !== "/create"}
                isClickable={false}
              >
                {t("createListingLayout.propertyDetails")}
              </Stepper.Step>
              <Stepper.Step
                href="/create/confirmation"
                stepNo={2}
                isCurrentStep={pathname === "/create/confirmation"}
                isComplete={false}
                isClickable={false}
              >
                {t("createListingLayout.confirmation")}
              </Stepper.Step>
            </Stepper>
          </Stack>
          <div className={styles.DesktopActionButtonContainer}>
            <Stack direction="column" gap={4}>
              {error && (
                <ErrorDisplay error={error} />
              )}
              <Button
                variant="primary"
                onClick={action.onClick}
                isLoading={action.isLoading}
                fullWidth
              >
                {action.text}
              </Button>
            </Stack>
          </div>
        </BodyFrame>
      </Sidebar>
      <BodyFrame className={styles.Children}>
        {children}
        <div className={styles.MobileActionButtonContainer}>
          <Stack direction="column" gap={4}>
            {error && (
              <ErrorDisplay error={error} />
            )}
            <Button
              variant="primary"
              onClick={action.onClick}
              isLoading={action.isLoading}
              fullWidth
            >
              {action.text}
            </Button>
          </Stack>
        </div>
      </BodyFrame>
    </div>
  );
};
